import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from "classnames";
import style from "../common.module.scss"
import { useNavigate } from "react-router-dom";
import moment from "moment";
import 'moment/locale/ru'
import ArrowDownIcon from '../../../../shared/icons/ArrowDownIcon';
import OrderInfo from '../../../order-info/OrderInfo';
import OrdersSectionItemTableStatus from '../../../section-item/components/OrdersSectionItemTableStatus';
import { useTranslation } from 'react-i18next';
import OrderInfoBookingCard from '../../../order-info/OrderInfoBookingCard';
import NotificationIconNew from '../../../../shared/icons/NotificationIconNew';
import NotificationsPopup from '../../../../notification/NotificationsPopup/NotificationsPopup';
import LineFlexIcon from '../../../../shared/icons/LineFlexIcon';

moment.locale("ru")

const OrdersTableMobile = ({
  items = [],
  isExpeditor = false,
  isRailway = false,
  onOpenOrder,
  isSearchPage = false,
  isShowOrderSolution = false,
  notificationsMap = {},
  setNotificationsMap,
  isMarketplacePage = false
}) => {
  const navigate = useNavigate();
  const tableRef = useRef(null);
  const { t } = useTranslation();
  const notificationRefs = useRef([]);

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [openNotificationOrderId, setOpenNotificationOrderId] = useState(null);

  const handleToggleNotification = (orderId) => {
    setOpenNotificationOrderId((prevId) => (prevId === orderId ? null : orderId));
  };

  const handleOpenOrderInfo = (orderInfo, orderId) => {
    setSelectedOrderId(null)
    setSelectedOrderId(orderId);
    setOrderInfo(orderInfo);
  };

  const tableHead = useMemo(() => {
    return [
      {
        name: t("ordersAvailablePage.tableHead.fromToLabel"),
        isSortable: false,
        key: "addressFromTo",
      },
      {
        name: t("ordersAvailablePage.tableHead.status"),
        isSortable: false,
        key: "status",
      },
      {
        name: "",
        isSortable: false,
      }
    ]
  }, [isExpeditor, t]);

  return (
    <div className={cn(style.mobile_wrapper, "table-responsive")} ref={tableRef}>
      <table className="table">
        <thead>
          <tr>
            {tableHead.map((item, idx) => (
              <th scope="col" key={"head-" + idx}>
                <div className={style.tableHead}>
                  {item.component ? item.component : item.name}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            if (!notificationRefs.current[idx]) {
              notificationRefs.current[idx] = React.createRef();
            }
            const unreadCount = (notificationsMap[item.id] || []).filter(n => !n.isRead).length;
                      
          return (
            <tr
              key={idx}
              className="cursor-pointer"
            >
              <td
                onClick={() => {
                  if (onOpenOrder) {
                    onOpenOrder(item.id);
                  } else if (isSearchPage) {
                    navigate(`/search/order/${item.id}`, {
                      state: { isSearchPage: true },
                    });
                  } else if (isMarketplacePage) {
                    navigate(`/marketplace/info/order/${item.id}`);
                  } else {
                    navigate(`/info/order/${item.id}`, {
                      state: { isShowOrderSolution: true },
                    });
                  }
                }}
                className={cn("cursor-pointer", style.mobile_wrapper_col)}
              >
                <LineFlexIcon height={70} />
                <div className={style.table_col}>
                  {isRailway && (
                    <span className={cn(style.table_col_summary, style.railwayWagon)}>
                      {t("railwayOrder.wagonType")}: {item.wagonTypeName || "—"}
                    </span>
                  )}
                  <div className={style.table_col_country}>
                    {item.cityFromName}
                  </div>
                  <span className={style.table_col_summary}>
                      {moment
                        .utc(item.shippingDate)
                        .startOf("day")
                        .add(item.shippingHourFrom, "hours")
                        .format("D MMMM, HH:mm")}
                    </span>
                  <div className={style.table_col_country}>
                    {item.cityToName}
                  </div>
                  <span className={style.table_col_summary}>
                    {item?.unloadingDate
                      ? moment
                          .utc(item.unloadingDate)
                          .startOf("day")
                          .add(item.unloadingHourFrom, "hours")
                          .format("D MMMM, HH:mm")
                      : t("orderInfo.ordersInfoDirections.awaitingDateAssignment")}
                  </span>
                </div>
              </td>
              <td 
                onClick={() => {
                  if (onOpenOrder) {
                    onOpenOrder(item.id);
                  } else if (isSearchPage) {
                    navigate(`/search/order/${item.id}`, {
                      state: { isSearchPage: true },
                    });
                  } else if (isMarketplacePage) {
                    navigate(`/marketplace/info/order/${item.id}`);
                  } else {
                    navigate(`/info/order/${item.id}`, {
                      state: { isShowOrderSolution: true },
                    });
                  }
                }}
              >
                <div>
                  <OrdersSectionItemTableStatus
                    timeSnapshotKey={item}
                    tenderDateEnd={item?.tenderDateEnd}
                    status={item.status}
                    driverName={item.driverName}
                    driverSurname={item.driverSurname}
                    assignmentMethod={item?.assignmentMethod}
                  />
                </div>
              </td>
              <td>
                <div className={style.table_col_arrow}>
                  <div
                    className={cn("ms-auto", style.notification)}
                    ref={notificationRefs.current[idx]}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleNotification(item.id);
                    }}
                  >
                    <NotificationIconNew />
                    {unreadCount > 0 && (
                      <div className={style.notification_count}>{unreadCount}</div>
                    )}
                  </div>

                  <NotificationsPopup
                    notifications={notificationsMap[item.id] || []}
                    orderData={item}
                    orderType="order"
                    isOpen={openNotificationOrderId === item.id}
                    onClose={() => {
                      setOpenNotificationOrderId(null);
                    }}
                    referenceElement={notificationRefs.current[idx]?.current}
                    onNotificationsRead={(readIds) => {
                      setNotificationsMap((prev) => ({
                        ...prev,
                        [item.id]: prev[item.id].map((notif) =>
                          readIds.includes(notif.id)
                            ? { ...notif, isRead: true, readDate: new Date() }
                            : notif
                        ),
                      }));
                    }}
                  />
                </div>
              </td>
            </tr>
          )})}
        </tbody>
      </table>
    </div>
  );
};

export default OrdersTableMobile;
